import { createContext, useCallback, useContext, useState } from 'react'

import { FormikErrors } from 'formik'
import {
  FinancialWorkspacePayrollConceptsDocument,
  FinancialWorkspaceUpdateSettingDocument,
  GetFinancialWorkspaceSettingsDocument,
  PayrollConceptFragment,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  DEFAULT_CONCEPT_NAMES,
  FinancialWorkspaceSettingsFormValues,
  LABOR_COST_CONCEPT_CALCULATION_TYPES,
} from './constants'

const getConceptIdsByCalculationType = (
  laborCostConcepts: Array<{ conceptId: number; laborCostCalculationType: string }> | undefined,
  calculationType: string
) => {
  return (
    laborCostConcepts
      ?.filter((concept) => concept.laborCostCalculationType === calculationType)
      .map((concept) => Number(concept.conceptId)) || []
  )
}

interface FinancialWorkspaceSettingsContextValue {
  values: FinancialWorkspaceSettingsFormValues
  setFieldValue: (field: keyof FinancialWorkspaceSettingsFormValues, value: number[]) => void
  dirty: boolean
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  loading: boolean
  errors?: FormikErrors<FinancialWorkspaceSettingsFormValues>
  showBanner: boolean
  showResetButton: boolean
  handleReset: () => void
  companyCostConcept?: PayrollConceptFragment
  expensesConcept?: PayrollConceptFragment
  isResetting: boolean
  defaultConceptIdsToAdd: number[]
  defaultConceptIdsToSubtract: number[]
  expensesConceptMissing: boolean
  setIsResetting: (isResetting: boolean) => void
  setExpensesConceptMissing: (missing: boolean) => void
}

const FinancialWorkspaceSettingsContext = createContext<
  FinancialWorkspaceSettingsContextValue | undefined
>(undefined)

interface FinancialWorkspaceSettingsProviderProps {
  children: React.ReactNode
}

export const FinancialWorkspaceSettingsProvider: React.FC<
  FinancialWorkspaceSettingsProviderProps
> = ({ children }) => {
  const [isResetting, setIsResetting] = useState<boolean>(false)
  const [expensesConceptMissing, setExpensesConceptMissing] = useState<boolean>(false)

  const {
    data: financialWorkspaceSettings,
    loading: financialWorkspaceSettingsLoading,
    refetch: refetchFinancialWorkspaceSettings,
  } = useQuery(GetFinancialWorkspaceSettingsDocument)

  const { data: concepts, loading: conceptsLoading } = useQuery(
    FinancialWorkspacePayrollConceptsDocument,
    {
      variables: {
        default: true,
        names: [DEFAULT_CONCEPT_NAMES.EXPENSES, DEFAULT_CONCEPT_NAMES.COMPANY_COST],
      },
    }
  )

  const companyDefaultConcepts = concepts?.payroll?.conceptsConnection?.edges.map(
    (edge) => edge.node
  )

  const currentFinancialWorkspaceSettings =
    financialWorkspaceSettings?.financialWorkspace?.settingsConnection?.edges[0]?.node

  const [updateFinancialWorkspaceSetting, { loading: updateLoading }] = useMutation(
    FinancialWorkspaceUpdateSettingDocument,
    { extractorFn: (data) => data.financialWorkspaceMutations.updateFinancialworkspaceSetting }
  )

  const defaultConceptIdsToAdd = getConceptIdsByCalculationType(
    currentFinancialWorkspaceSettings?.laborCostConcepts,
    LABOR_COST_CONCEPT_CALCULATION_TYPES.ADD_TO_TOTAL
  )

  const defaultConceptIdsToSubtract = getConceptIdsByCalculationType(
    currentFinancialWorkspaceSettings?.laborCostConcepts,
    LABOR_COST_CONCEPT_CALCULATION_TYPES.SUBTRACT_FROM_TOTAL
  )

  const validateLaborCostConcepts = (data: FinancialWorkspaceSettingsFormValues) => {
    const errors: Record<string, string> = {}
    const errorMessage = i18n.t(
      'finance.financial_workspace.settings.labor_cost.errors.duplicate_concept_selection'
    )

    const noAddToTotalConceptsErrorMessage = i18n.t(
      'finance.financial_workspace.settings.labor_cost.errors.no_add_to_total_concepts'
    )

    if (data.conceptIdsToAdd.length === 0) {
      return {
        ...errors,
        conceptIdsToAdd: noAddToTotalConceptsErrorMessage,
      }
    }

    // Validate for duplicates within each array
    const addDuplicates = data.conceptIdsToAdd.filter(
      (id, index) => data.conceptIdsToAdd.indexOf(id) !== index
    )
    const subtractDuplicates = data.conceptIdsToSubtract.filter(
      (id, index) => data.conceptIdsToSubtract.indexOf(id) !== index
    )

    // Validate for overlap between arrays
    const overlap = data.conceptIdsToAdd.filter((id) => data.conceptIdsToSubtract.includes(id))

    const hasAddIssues = addDuplicates.length > 0 || overlap.length > 0
    const hasSubtractIssues = subtractDuplicates.length > 0 || overlap.length > 0

    if (hasAddIssues) {
      errors.conceptIdsToAdd = errorMessage
    }

    if (hasSubtractIssues) {
      errors.conceptIdsToSubtract = errorMessage
    }

    return errors
  }

  const handleSubmitForm = useCallback(
    async (
      data: FinancialWorkspaceSettingsFormValues,
      { setErrors }: { setErrors: (errors: Record<string, string>) => void }
    ) => {
      const toAddConcepts = data.conceptIdsToAdd
      const laborCostConceptsToAdd = toAddConcepts.map((conceptId) => ({
        conceptId,
        laborCostCalculationType: LABOR_COST_CONCEPT_CALCULATION_TYPES.ADD_TO_TOTAL,
      }))

      const toSubtractConcepts = data.conceptIdsToSubtract
      const laborCostConceptsToSubtract = toSubtractConcepts.map((conceptId) => ({
        conceptId,
        laborCostCalculationType: LABOR_COST_CONCEPT_CALCULATION_TYPES.SUBTRACT_FROM_TOTAL,
      }))

      const result = await updateFinancialWorkspaceSetting({
        variables: {
          id: Number(currentFinancialWorkspaceSettings?.id),
          laborCostConcepts: [...laborCostConceptsToAdd, ...laborCostConceptsToSubtract],
        },
      })

      const handled = handleResult(result, (resultErrors) => {
        app.setFlash({
          type: 'wrong',
          message: resultErrors.laborCostConcepts ?? i18n.t('errors.generic_error'),
        })
        setErrors(resultErrors)
      })

      if (handled.success) {
        refetchFinancialWorkspaceSettings()
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
      setIsResetting(false)

      return handled
    },
    [
      currentFinancialWorkspaceSettings?.id,
      refetchFinancialWorkspaceSettings,
      updateFinancialWorkspaceSetting,
    ]
  )

  const { values, errors, setFieldValue, dirty, handleSubmit, resetForm } =
    useForm<FinancialWorkspaceSettingsFormValues>({
      enableReinitialize: true,
      defaultValues: {
        conceptIdsToAdd: defaultConceptIdsToAdd,
        conceptIdsToSubtract: defaultConceptIdsToSubtract,
      },
      validate: validateLaborCostConcepts,
      async onSubmit(data, { setErrors }) {
        const result = await handleSubmitForm(data, { setErrors })
        if (result.success) {
          resetForm({ values })
        }
        return result
      },
    })

  const showBanner = expensesConceptMissing && dirty
  const loading = updateLoading || financialWorkspaceSettingsLoading

  const companyCostConcept = companyDefaultConcepts?.find(
    (concept) => concept.name === DEFAULT_CONCEPT_NAMES.COMPANY_COST
  )
  const expensesConcept = companyDefaultConcepts?.find(
    (concept) => concept.name === DEFAULT_CONCEPT_NAMES.EXPENSES
  )

  // Check if the current selection is the default selection
  const currentSelectionIsDefault = useUnsafeMemo(() => {
    if (conceptsLoading) return false

    const hasAddConcept = values.conceptIdsToAdd.length > 0
    const hasSubtractConcept = values.conceptIdsToSubtract.length > 0

    if (!hasAddConcept || !hasSubtractConcept) return false

    return (
      values.conceptIdsToAdd.every((conceptId) => companyCostConcept?.id === conceptId) &&
      values.conceptIdsToSubtract.every((conceptId) => expensesConcept?.id === conceptId)
    )
  }, [
    conceptsLoading,
    values.conceptIdsToAdd,
    values.conceptIdsToSubtract,
    companyCostConcept,
    expensesConcept,
  ])

  const handleReset = () => {
    if (!companyDefaultConcepts?.length) return

    setIsResetting(true)

    if (companyCostConcept) {
      setFieldValue('conceptIdsToAdd', [Number(companyCostConcept.id)])
    }
    if (expensesConcept) {
      setFieldValue('conceptIdsToSubtract', [Number(expensesConcept.id)])
    }
  }

  const showResetButton = !currentSelectionIsDefault
  const value: FinancialWorkspaceSettingsContextValue = {
    values,
    setFieldValue,
    dirty,
    handleSubmit,
    loading,
    errors,
    showBanner,
    showResetButton,
    handleReset,
    isResetting,
    companyCostConcept,
    expensesConcept,
    setIsResetting,
    defaultConceptIdsToAdd,
    defaultConceptIdsToSubtract,
    expensesConceptMissing,
    setExpensesConceptMissing,
  }

  return (
    <FinancialWorkspaceSettingsContext.Provider value={value}>
      {children}
    </FinancialWorkspaceSettingsContext.Provider>
  )
}

export const useFinancialWorkspaceSettings = () => {
  const context = useContext(FinancialWorkspaceSettingsContext)
  if (context === undefined) {
    throw new Error(
      'useFinancialWorkspaceSettings must be used within a FinancialWorkspaceSettingsProvider'
    )
  }
  return context
}
